import React, {useState} from 'react';
import {Link, useNavigate} from 'react-router-dom';
import './Auth.css';
// Firebase imports - user will configure Firebase
import { auth } from '../firebase/config'; // User will set this up
import { GoogleAuthProvider, signInWithPopup, createUserWithEmailAndPassword } from 'firebase/auth';
import API, { coreApi } from '../api/api';
import Navigation from '../components/Navigation';
import COUNTRIES from '../utils/countries';

const Signup = () => {
    const navigate = useNavigate();
    const [currentStep, setCurrentStep] = useState(1);
    const [formData, setFormData] = useState({
        username: '',
        firstName: '',
        lastName: '',
        birthDate: '',
        country: '',
        linkedin: '',
        twitter: '',
        email: '',
        password: '',
        confirmPassword: '',
        acceptPrivacyPolicy: false
    });

    const [errors, setErrors] = useState({});
    const [isCheckingUsername, setIsCheckingUsername] = useState(false);
    const [isSigningUp, setIsSigningUp] = useState(false);
    const [usernameCheckResult, setUsernameCheckResult] = useState(null);
    const [successMessage, setSuccessMessage] = useState('');

    const handleInputChange = (e) => {
        const {name, value, type, checked} = e.target;
        /* A checkbox carries its answer on `checked`; `value` is the string "on"
           whichever way it is pointing. Read off `value`, the privacy tick was
           recorded the moment it was first ticked and stayed recorded after it
           was unticked — the form let the account through and told core-api the
           policy had been accepted. */
        setFormData({
            ...formData,
            [name]: type === 'checkbox' ? checked : value
        });

        // Clear specific error when user starts typing
        if (errors[name]) {
            setErrors({
                ...errors,
                [name]: ''
            });
        }

        // Clear username check result when username changes
        if (name === 'username') {
            setUsernameCheckResult(null);
        }
    };

    const calculateAge = (birthDate) => {
        const today = new Date();
        const birth = new Date(birthDate);
        let age = today.getFullYear() - birth.getFullYear();
        const monthDiff = today.getMonth() - birth.getMonth();

        if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
            age--;
        }

        return age;
    };

    const checkUsernameUniqueness = async (username) => {
        if (!username.trim()) {
            return false;
        }

        setIsCheckingUsername(true);
        try {
            const response = await API.get(`/users/check-unique-username?username=${encodeURIComponent(username)}`);
            
            if (response.data) {
                setUsernameCheckResult(response.data.unique);
                return response.data.unique;
            }
            setErrors(prev => ({
                ...prev,
                username: 'We could not check that username. Please try again.'
            }));
            // Not `false`: a check that did not happen is not an answer, and the
            // caller used to turn it into "Username is already taken".
            return null;
        } catch (error) {
            console.error('Error checking username:', error);
            setErrors(prev => ({
                ...prev,
                username: 'We could not check that username. Please try again.'
            }));
            return null;
        } finally {
            setIsCheckingUsername(false);
        }
    };

    const validateStep1 = async () => {
        const newErrors = {};

        // Required field validations
        if (!formData.username.trim()) {
            newErrors.username = 'Username is required';
        }
        if (!formData.firstName.trim()) {
            newErrors.firstName = 'First name is required';
        }
        if (!formData.lastName.trim()) {
            newErrors.lastName = 'Last name is required';
        }
        if (!formData.birthDate) {
            newErrors.birthDate = 'Birth date is required';
        } else {
            const age = calculateAge(formData.birthDate);
            if (age < 18) {
                newErrors.birthDate = 'You must be at least 18 years old to sign up';
            }
        }
        if (!formData.country.trim()) {
            newErrors.country = 'Country is required';
        }

        setErrors(newErrors);

        // If basic validation fails, don't proceed
        if (Object.keys(newErrors).length > 0) {
            return false;
        }

        // Check username uniqueness
        const isUsernameUnique = await checkUsernameUniqueness(formData.username);
        if (isUsernameUnique === false) {
            setErrors(prev => ({
                ...prev,
                username: 'Username is already taken'
            }));
        }
        if (!isUsernameUnique) {
            // A null means the check itself failed and has already said so.
            return false;
        }

        return true;
    };

    /* `skipPassword` is the Google path: Google supplies the address and holds the
       password, so the three credential fields are not part of that form. The
       privacy tick is part of both. */
    const validateStep2 = ({ skipPassword = false } = {}) => {
        const newErrors = {};

        if (!skipPassword) {
            // Email validation
            if (!formData.email.trim()) {
                newErrors.email = 'Email is required';
            } else {
                const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                if (!emailRegex.test(formData.email)) {
                    newErrors.email = 'Please enter a valid email address';
                }
            }

            // Password validation
            if (!formData.password) {
                newErrors.password = 'Password is required';
            } else if (formData.password.length < 6) {
                newErrors.password = 'Password must be at least 6 characters long';
            }

            // Confirm password validation
            if (!formData.confirmPassword) {
                newErrors.confirmPassword = 'Please confirm your password';
            } else if (formData.password !== formData.confirmPassword) {
                newErrors.confirmPassword = 'Passwords do not match';
            }
        }

        // Privacy policy validation
        if (!formData.acceptPrivacyPolicy) {
            newErrors.acceptPrivacyPolicy = 'You must accept the Privacy Policy to continue';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const goToStep2 = async () => {
        const isValid = await validateStep1();
        if (isValid) {
            setCurrentStep(2);
        }
    };

    const goToStep1 = () => {
        setCurrentStep(1);
    };

    const handleEmailPasswordSignup = async () => {
        if (!validateStep2()) {
            return;
        }

        setIsSigningUp(true);
        try {
            // Create user with email and password
            const userCredential = await createUserWithEmailAndPassword(auth, formData.email, formData.password);
            const user = userCredential.user;
            
            // Verification email will be sent by the backend
            
            // Get the Firebase ID token
            const idToken = await user.getIdToken();
            
            // Submit signup with Firebase token - specify it's email signup
            await submitSignup(idToken, 'email');
            
        } catch (error) {
            console.error('Email/Password signup error:', error);
            
            // Handle Firebase auth errors
            let errorMessage = 'Failed to create account. Please try again.';
            
            switch (error.code) {
                case 'auth/email-already-in-use':
                    errorMessage = 'An account with this email already exists.';
                    break;
                case 'auth/invalid-email':
                    errorMessage = 'Please enter a valid email address.';
                    break;
                case 'auth/weak-password':
                    errorMessage = 'Password is too weak. Please choose a stronger password.';
                    break;
                default:
                    errorMessage = error.message || errorMessage;
            }
            
            setErrors(prev => ({
                ...prev,
                auth: errorMessage
            }));
        } finally {
            setIsSigningUp(false);
        }
    };

    const handleGoogleSignup = async () => {
        /* The same gate the email button passes. Without it, "Continue with
           Google" walked past the privacy tick entirely: core-api refused the
           account (and deleted the Firebase user it had just made), and the
           candidate was shown the server's sentence instead of the unticked box
           that caused it. */
        if (!validateStep2({ skipPassword: true })) {
            return;
        }

        setIsSigningUp(true);
        try {
            const provider = new GoogleAuthProvider();
            const result = await signInWithPopup(auth, provider);
            const user = result.user;
            
            // Get the Firebase ID token
            const idToken = await user.getIdToken();
            
            // Submit signup with Firebase token - specify it's Google signup
            await submitSignup(idToken, 'google');
            
        } catch (error) {
            console.error('Google signup error:', error);
            setErrors(prev => ({
                ...prev,
                auth: 'Failed to sign up with Google. Please try again.'
            }));
        } finally {
            setIsSigningUp(false);
        }
    };

    // Show success message
    const showSuccess = (message) => {
        setSuccessMessage(message);
        setErrors({});
        setTimeout(() => setSuccessMessage(''), 5000);
    };

    const submitSignup = async (firebaseAuthToken, signupMethod = 'email') => {
        try {
            const signupData = {
                username: formData.username,
                firstname: formData.firstName,
                lastname: formData.lastName,
                birthdate: formData.birthDate,
                country: formData.country.toLowerCase().trim(),
                linkedin_profile: formData.linkedin || '',
                twitter_handle: formData.twitter || '',
                firebase_auth_token: firebaseAuthToken,
                privacy_policy_acceptance: !!(formData.acceptPrivacyPolicy)
            };

            const response = await coreApi.post('/auth/signup', signupData);

            if (response.data) {
                // Signup successful - show different messages based on signup method
                console.log('Signup successful:', response.data);
                
                let successMessage;
                if (signupMethod === 'google') {
                    successMessage = 'Account created successfully! You can now log in to your account.';
                } else {
                    successMessage = response.data.message || 'Account created successfully! Please check your email for a verification link. If you don\'t see it in your inbox, please check your spam folder.';
                }
                
                showSuccess(successMessage);
                // Redirect to login page after a delay
                setTimeout(() => navigate('/login'), 3000);
            }
        } catch (error) {
            console.error('Signup error:', error);
            
            // Handle error response from server
            if (error.response && error.response.data && error.response.data.error) {
                setErrors(prev => ({
                    ...prev,
                    auth: error.response.data.error
                }));
            } else {
                setErrors(prev => ({
                    ...prev,
                    auth: 'Network error. Please try again.'
                }));
            }
        }
    };

    return (
        <>
            <Navigation />
            <div className="auth-page auth-page-with-nav">
                <div className="auth-container signup-container">
                    <div className="auth-card">
                        <div className="auth-header">
                            <img
                                src="/images/appli-logo-black.png"
                                alt="Appli Agent Logo"
                                className="auth-logo"
                            />
                            <h1>Create Your Account</h1>

                            {/* Step Indicator */}
                            <div className="step-indicator">
                                <div
                                    className={`step ${currentStep === 1 ? 'active' : currentStep > 1 ? 'completed' : ''}`}>
                                    <div className="step-number">1</div>
                                    <div className="step-text">Personal Information</div>
                                </div>
                                <div className={`step ${currentStep === 2 ? 'active' : ''}`}>
                                    <div className="step-number">2</div>
                                    <div className="step-text">Create Account</div>
                                </div>
                            </div>
                        </div>

                        {/* Error/Success Messages */}
                        {errors.auth && (
                            <div className="error-message" style={{display: 'block'}}>
                                {errors.auth}
                            </div>
                        )}
                        {successMessage && (
                            <div className="success-message" style={{display: 'block'}}>
                                {successMessage}
                            </div>
                        )}

                        {/* Step 1: Personal Information */}
                        {currentStep === 1 && (
                            <div id="step-1" className="signup-step">
                                <p>Tell us about yourself to get started.</p>

                                <form id="user-info-form" className="auth-form">
                                    <div className="form-grid">
                                        <div className="form-field full-width">
                                            <input
                                                type="text"
                                                id="username"
                                                name="username"
                                                placeholder="Username"
                                                value={formData.username}
                                                onChange={handleInputChange}
                                                required
                                                className={errors.username ? 'error' : ''}
                                            />
                                            {errors.username && <div className="field-error">{errors.username}</div>}
                                            {isCheckingUsername && (
                                                <div className="username-validation checking">
                                                    Checking username availability...
                                                </div>
                                            )}
                                            {usernameCheckResult === true && (
                                                <div className="username-validation available">
                                                    ✓ Username is available
                                                </div>
                                            )}
                                            {usernameCheckResult === false && (
                                                <div className="username-validation taken">
                                                    ✗ Username is already taken
                                                </div>
                                            )}
                                        </div>

                                        <div className="form-field">
                                            <input
                                                type="text"
                                                id="firstName"
                                                name="firstName"
                                                placeholder="First Name"
                                                value={formData.firstName}
                                                onChange={handleInputChange}
                                                required
                                                className={errors.firstName ? 'error' : ''}
                                            />
                                            {errors.firstName && <div className="field-error">{errors.firstName}</div>}
                                        </div>

                                        <div className="form-field">
                                            <input
                                                type="text"
                                                id="lastName"
                                                name="lastName"
                                                placeholder="Last Name"
                                                value={formData.lastName}
                                                onChange={handleInputChange}
                                                required
                                                className={errors.lastName ? 'error' : ''}
                                            />
                                            {errors.lastName && <div className="field-error">{errors.lastName}</div>}
                                        </div>

                                        <div className="form-field full-width">
                                            {/* Every other field names itself with a placeholder;
                                                `input type="date"` shows none, so this one had no
                                                name at all — just "mm/dd/yyyy". */}
                                            <label className="field-label" htmlFor="birthDate">Date of birth</label>
                                            <input
                                                type="date"
                                                id="birthDate"
                                                name="birthDate"
                                                placeholder="Birth Date"
                                                value={formData.birthDate}
                                                onChange={handleInputChange}
                                                required
                                                className={errors.birthDate ? 'error' : ''}
                                                max={new Date(new Date().setFullYear(new Date().getFullYear() - 18)).toISOString().split('T')[0]}
                                            />
                                            {errors.birthDate && <div className="field-error">{errors.birthDate}</div>}
                                            <div className="field-help">You must be at least 18 years old</div>
                                        </div>

                                        <div className="form-field full-width">
                                            <select
                                                id="country"
                                                name="country"
                                                value={formData.country}
                                                onChange={handleInputChange}
                                                required
                                                className={errors.country ? 'error' : ''}
                                            >
                                                <option value="">Select Country</option>
                                                {COUNTRIES.map((c) => (
                                                    <option key={c} value={c}>{c}</option>
                                                ))}
                                            </select>
                                            {errors.country && <div className="field-error">{errors.country}</div>}
                                        </div>

                                        <div className="form-field full-width">
                                            <input
                                                type="url"
                                                id="linkedin"
                                                name="linkedin"
                                                placeholder="LinkedIn Profile URL (Optional)"
                                                value={formData.linkedin}
                                                onChange={handleInputChange}
                                            />
                                            <div className="field-help">e.g., https://linkedin.com/in/yourprofile</div>
                                        </div>

                                        <div className="form-field full-width">
                                            <input
                                                type="text"
                                                id="twitter"
                                                name="twitter"
                                                placeholder="Twitter / X Handle (Optional)"
                                                value={formData.twitter}
                                                onChange={handleInputChange}
                                            />
                                            <div className="field-help">e.g., @yourusername</div>
                                        </div>
                                    </div>
                                    <button 
                                        type="button" 
                                        onClick={goToStep2} 
                                        className="auth-button next-step-btn"
                                        disabled={isCheckingUsername}
                                    >
                                        {isCheckingUsername ? 'Checking...' : 'Next Step'}
                                    </button>
                                </form>
                            </div>
                        )}

                        {/* Step 2: Account Creation */}
                        {currentStep === 2 && (
                            <div id="step-2" className="signup-step">
                                <p>Choose how you'd like to create your account.</p>

                                <div className="email-password-section">
                                    <h3 className="auth-section-title">Create Account with Email</h3>
                                    
                                    <div className="form-field">
                                        <input
                                            type="email"
                                            id="email"
                                            name="email"
                                            placeholder="Email Address"
                                            value={formData.email}
                                            onChange={handleInputChange}
                                            required
                                            className={errors.email ? 'error' : ''}
                                        />
                                        {errors.email && <div className="field-error">{errors.email}</div>}
                                    </div>

                                    <div className="form-field">
                                        <input
                                            type="password"
                                            id="password"
                                            name="password"
                                            placeholder="Password"
                                            value={formData.password}
                                            onChange={handleInputChange}
                                            required
                                            className={errors.password ? 'error' : ''}
                                        />
                                        {errors.password && <div className="field-error">{errors.password}</div>}
                                        <div className="password-requirements">
                                            Password must be at least 6 characters long
                                        </div>
                                    </div>

                                    <div className="form-field">
                                        <input
                                            type="password"
                                            id="confirmPassword"
                                            name="confirmPassword"
                                            placeholder="Confirm Password"
                                            value={formData.confirmPassword}
                                            onChange={handleInputChange}
                                            required
                                            className={errors.confirmPassword ? 'error' : ''}
                                        />
                                        {errors.confirmPassword && <div className="field-error">{errors.confirmPassword}</div>}
                                    </div>

                                    <div className="form-field full-width">
                                        <div className="privacy-policy-checkbox">
                                            <label className="checkbox-container">
                                                <input
                                                    type="checkbox"
                                                    name="acceptPrivacyPolicy"
                                                    checked={!!formData.acceptPrivacyPolicy}
                                                    onChange={handleInputChange}
                                                    className={errors.acceptPrivacyPolicy ? 'error' : ''}
                                                />
                                                <span className="checkmark"></span>
                                                <span className="checkbox-text">
                                                    {/* The portal's own copy of the policy, not the marketing
                                                        site's: this link pointed at production from every
                                                        build, so a dev tester read a different document from
                                                        the one this form is asking about. */}
                                                    I accept the <Link to="/privacy-policy" target="_blank" rel="noopener noreferrer" className="privacy-link">Privacy Policy</Link>
                                                </span>
                                            </label>
                                        </div>
                                        {errors.acceptPrivacyPolicy && <div className="field-error">{errors.acceptPrivacyPolicy}</div>}
                                    </div>

                                    <button 
                                        type="button" 
                                        className="auth-button"
                                        onClick={handleEmailPasswordSignup}
                                        disabled={isSigningUp}
                                    >
                                        {isSigningUp ? 'Creating Account...' : 'Create Account'}
                                    </button>

                                    <div className="auth-divider">
                                        <span>or</span>
                                    </div>

                                    <button 
                                        type="button" 
                                        className="google-signup-btn"
                                        onClick={handleGoogleSignup}
                                        disabled={isSigningUp}
                                    >
                                        <svg className="google-icon" viewBox="0 0 24 24">
                                            <path fill="#4285F4"
                                                  d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                                            <path fill="#34A853"
                                                  d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                                            <path fill="#FBBC05"
                                                  d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                                            <path fill="#EA4335"
                                                  d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                                        </svg>
                                        {isSigningUp ? 'Creating Account...' : 'Continue with Google'}
                                    </button>
                                </div>

                                <button type="button" onClick={goToStep1} className="back-btn">
                                    Back
                                </button>
                            </div>
                        )}

                        <div className="auth-footer">
                            <p>
                                Already have an account? <Link to="/login">Login</Link>
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default Signup;
